import { useState, useEffect, useContext } from "react";
import "./common.scss";
import FormRendererContext from "../../services/context/FormRendererContext";
import { currentFormattedDateAndTime, trackEvent } from "../../utils/mixPanel";

const HeightField = ({ data, setAnswer, formValidation = true, formClass }) => {
  const [feet, setFeet] = useState("");
  const [inches, setInches] = useState("");
  const [validForm, setFormValidation] = useState(formValidation);
  const { answerData } = useContext(FormRendererContext);

  // Convert total inches to feet and inches
  const convertInchesToFeetAndInches = (totalInches) => {
    if (!totalInches || totalInches === "") return { feet: "", inches: "" };
    const totalInchesNum = parseInt(totalInches);
    const feetValue = Math.floor(totalInchesNum / 12);
    const inchesValue = totalInchesNum % 12;
    return { 
      feet: feetValue > 0 ? feetValue.toString() : "", 
      inches: inchesValue > 0 ? inchesValue.toString() : "" 
    };
  };

  // Convert feet and inches to total inches
  const convertToTotalInches = (feetValue, inchesValue) => {
    const feetNum = parseInt(feetValue) || 0;
    const inchesNum = parseInt(inchesValue) || 0;
    return feetNum * 12 + inchesNum;
  };

  // Handle focus events
  const handleFocus = (fieldType) => {
    trackEvent(`height-field ${data?.label} ${fieldType} clicked`, {
      question: data?.label,
      questionKey: data?.key,
      clickedAt: currentFormattedDateAndTime(),
    });
  };

  // Handle blur events
  const handleBlur = (fieldType) => {
    trackEvent(`height-field ${data?.label} ${fieldType} filled`, {
      question: data?.label,
      questionKey: data?.key,
      clickedAt: currentFormattedDateAndTime(),
      data: "Answered",
    });
  };

  // Handle feet input change
  const handleFeetChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Only allow digits
    const limitedValue = value.slice(0, 2); // Limit to 2 digits (max 99 feet)
    
    setFeet(limitedValue);
    const totalInches = convertToTotalInches(limitedValue, inches);
    setAnswer(data, { value: totalInches.toString() });
    setFormValidation(true);
  };

  // Handle inches input change
  const handleInchesChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Only allow digits
    let limitedValue = parseInt(value) || 0;
    
    // If inches >= 12, convert to feet
    if (limitedValue >= 12) {
      const extraFeet = Math.floor(limitedValue / 12);
      const remainingInches = limitedValue % 12;
      const newFeet = (parseInt(feet) || 0) + extraFeet;
      
      setFeet(newFeet.toString());
      setInches(remainingInches > 0 ? remainingInches.toString() : "");
      
      const totalInches = convertToTotalInches(newFeet.toString(), remainingInches.toString());
      setAnswer(data, { value: totalInches.toString() });
    } else {
      setInches(limitedValue > 0 ? limitedValue.toString() : "");
      const totalInches = convertToTotalInches(feet, limitedValue.toString());
      setAnswer(data, { value: totalInches.toString() });
    }
    setFormValidation(true);
  };

  // Initialize component with existing data
  useEffect(() => {
    setFormValidation(formValidation);
    
    let initialValue = "";
    if (data?.answer?.value) {
      initialValue = data.answer.value;
    } else if (answerData?.[data?.key]?.value) {
      initialValue = answerData[data.key].value;
    }

    if (initialValue) {
      const { feet: feetValue, inches: inchesValue } = convertInchesToFeetAndInches(initialValue);
      setFeet(feetValue);
      setInches(inchesValue);
      setFormValidation(true);
    } else {
      setFeet("");
      setInches("");
    }
  }, [data, formValidation, answerData]);

  return (
    <div className={`w-full ${formClass}`}>
      <div className="form-title pt-3">
        {data?.label}
        {data?.validate?.required ? (
          <span className={!validForm ? "text-red-700" : "text-gray-600"}>
            {" "}
            *
          </span>
        ) : (
          <></>
        )}
      </div>
      {data?.validate?.required && !validForm ? (
        <span className="text-red-700 font-light text-xs">
          {data?.invalidMessage || "Required Field"}
        </span>
      ) : (
        <></>
      )}
      
      <form className="flex-grow">
        <div className="radio-common flex gap-2 items-center">
          {/* Feet Input */}
          <div className="flex-1">
            <label className="relative">
              <input
                type="text"
                disabled={data?.disabled || false}
                value={feet}
                onChange={handleFeetChange}
                onFocus={() => handleFocus("feet")}
                onBlur={() => handleBlur("feet")}
                placeholder="0"
                className="text-field text-input w-full"
                maxLength="2"
              />
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-md pointer-events-none">
                ft
              </div>
            </label>
          </div>

          {/* Inches Input */}
          <div className="flex-1">
            <label className="relative">
              <input
                type="text"
                disabled={data?.disabled || false}
                value={inches}
                onChange={handleInchesChange}
                onFocus={() => handleFocus("inches")}
                onBlur={() => handleBlur("inches")}
                placeholder="0"
                className="text-field text-input w-full"
                maxLength="2"
              />
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-md pointer-events-none">
                in
              </div>
            </label>
          </div>
        </div>
      </form>

      {data?.description && (
        <div className="text-xs text-gray-500 leading-relaxed mt-1">
          {data.description}
        </div>
      )}
    </div>
  );
};

export default HeightField;